import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge, Alert, Spinner, Form, Modal } from 'react-bootstrap';
import {
    Landmark,
    Plus,
    Trash2,
    AlertTriangle,
    RefreshCw,
    Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useLocation, useHistory } from 'react-router-dom';
import BankConnectModal, { BankLogo } from './BankConnectModal';
import BankImportWarning from './BankImportWarning';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { useFeatures } from '../../../context/FeatureContext';
import {
    fetchBanks,
    fetchConnections,
    startConnect,
    syncNow,
    setAccountEnabled,
    disconnect,
} from '../../../services/BankConnectionService';

/**
 * BankConnectionSettings — anslut företagets bankkonton via PSD2 (Enable
 * Banking) så att banktransaktioner hämtas automatiskt till Bankhändelser.
 *
 * Mirrors the E-postinkorg connect pattern with one extra dimension the
 * email flow doesn't have: PSD2 consents EXPIRE (typically 180 days, BankID
 * renewal required, no silent refresh). The card therefore always shows the
 * consent window and surfaces `expiring`/`expired` states with a renewal CTA
 * — lapsed consents are the #1 churn surface for bank feeds.
 *
 * SCA round-trip: "Anslut" → POST /authorize → full-page redirect to the
 * bank's BankID flow → backend /bank-feed/callback → 302 back HERE with
 * ?bank_connected=success|error&reason=<bounded enum>.
 */

const STATUS_BADGES = {
    // 'active' is the normal state — only exceptions are badged.
    expiring: { variant: 'warning', label: 'Förnyas snart' },
    expired: { variant: 'danger', label: 'Utgången' },
    error: { variant: 'danger', label: 'Fel' },
    pending_auth: { variant: 'secondary', label: 'Väntar på BankID' },
    revoked: { variant: 'secondary', label: 'Återkallad' },
    disconnected: { variant: 'secondary', label: 'Frånkopplad' },
};

const CALLBACK_REASONS = {
    invalid_state: 'Ogiltig begäran — försök ansluta igen.',
    sca_denied: 'BankID-signeringen avbröts eller nekades.',
    missing_code: 'Bankens svar var ofullständigt — försök igen.',
    exchange_failed: 'Kunde inte slutföra anslutningen mot banken.',
    company_not_found: 'Företaget kunde inte verifieras.',
    not_configured: 'Bankkoppling är inte aktiverad i denna miljö.',
};

const BACKFILL_LABELS = {
    pending: { label: 'Historik väntar', variant: 'secondary' },
    // 'running' renders a live spinner (see below); 'failed' is carried by
    // BankImportWarning (one signal, not a badge + a notice); 'done' renders nothing.
};

const digits = (str) => String(str || '').replace(/\D/g, '');
/** Same physical account when one digit-identity ends with the other (BBAN = clearing+konto). */
const sameAccount = (a, b) =>
    Boolean(a && b && (a === b || (a.length >= 7 && b.endsWith(a)) || (b.length >= 7 && a.endsWith(b))));

/**
 * Why a sync failed, in words the user can act on. The old catch-all
 * ("Hämtningen misslyckades") also fired while the backend was deliberately
 * cooling down after an earlier error, which read as a brand-new failure and
 * invited pointless retries — so a cooldown now says when we try again.
 */
function syncFailureMessage(result) {
    const retry = result?.retryAt
        ? ` Vi försöker igen ${formatDate(result.retryAt, 'HH:mm')}.`
        : ' Vi försöker igen automatiskt.';
    switch (result?.status === 'cooling_down' ? result.errorCode : result?.status) {
        case 'expired':
        case 'expired_session':
            return 'Bankens medgivande har gått ut — förnya anslutningen med BankID.';
        case 'rate_limited':
            return `Banken tillåter ett begränsat antal hämtningar per dygn och har pausat oss.${retry}`;
        case 'provider_busy':
            return `Banken kunde inte svara just nu.${retry}`;
        case 'not_configured':
            return 'Bankkoppling är inte aktiverad i denna miljö.';
        case 'not_found':
            return 'Anslutningen hittades inte — ladda om sidan.';
        default:
            return `Hämtningen misslyckades.${retry}`;
    }
}

function formatDate(dateStr, pattern = 'yyyy-MM-dd HH:mm') {
    if (!dateStr) return '—';
    try {
        return format(new Date(dateStr), pattern, { locale: sv });
    } catch {
        return dateStr;
    }
}

const BankConnectionSettings = ({ companyId }) => {
    const { entitlements } = useFeatures();
    const maxAccounts = entitlements?.limits?.maxBankAccounts;
    const allowMultiAccounts = maxAccounts === null || maxAccounts > 1;
    const [connections, setConnections] = useState([]);
    // Post-consent selection (multi-account consents): connection id from
    // ?select_accounts=, cleared when the user saves their choice.
    const [selectConnectionId, setSelectConnectionId] = useState(null);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [savingSelection, setSavingSelection] = useState(false);
    const [mappings, setMappings] = useState([]);
    const [banks, setBanks] = useState([]);
    const [providerConfigured, setProviderConfigured] = useState(true);
    const [showConnectModal, setShowConnectModal] = useState(false);
    const [banksLoading, setBanksLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const location = useLocation();
    const history = useHistory();

    const loadConnections = useCallback(async () => {
        try {
            setIsLoading(true);
            setConnections(await fetchConnections(companyId));
        } catch (err) {
            console.error('Error fetching bank connections:', err);
            toast.error('Kunde inte hämta bankanslutningar');
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        loadConnections();
    }, [loadConnections]);

    // Kontoplan mappings — so each feed account can show WHERE it books.
    useEffect(() => {
        axios
            .get(getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings`), { withCredentials: true })
            .then(({ data }) => setMappings(data.mappings || []))
            .catch(() => {});
    }, [companyId]);

    const mappingFor = (account) => {
        const acctId = digits(account.clearing_number) + digits(account.account_number);
        const bbanId = digits(account.bban);
        return mappings.find((m) => {
            const mId = digits(m.clearing_number) + digits(m.account_number);
            return sameAccount(mId, acctId) || sameAccount(mId, bbanId);
        });
    };

    // A running deep backfill finishes server-side within minutes — poll so
    // the spinner resolves to real data without the user refreshing.
    const anyBackfillRunning = connections.some((c) =>
        (c.accounts || []).some((a) => a.backfill_status === 'running')
    );
    useEffect(() => {
        if (!anyBackfillRunning) return undefined;
        const timer = setInterval(loadConnections, 5000);
        return () => clearInterval(timer);
    }, [anyBackfillRunning, loadConnections]);

    // Bank picker feed — loaded once; the backend caches the provider list.
    useEffect(() => {
        let cancelled = false;
        setBanksLoading(true);
        fetchBanks(companyId)
            .then((data) => {
                if (cancelled) return;
                setProviderConfigured(data.configured !== false);
                setBanks(data.banks || []);
            })
            .catch((err) => {
                console.error('Error fetching bank list:', err);
                if (!cancelled) setBanks([]);
            })
            .finally(() => {
                if (!cancelled) setBanksLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [companyId]);

    // SCA completion handler — toast once, then strip the params.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const status = params.get('bank_connected');
        if (!status) return;
        const reason = params.get('reason');

        if (status === 'success') {
            const selectId = params.get('select_accounts');
            if (selectId) {
                setSelectConnectionId(selectId);
                toast.success('Bankanslutning klar — välj vilka konton som ska synkas.');
            } else {
                toast.success('Bankanslutning klar — vi hämtar dina transaktioner i bakgrunden.');
            }
            loadConnections();
        } else {
            toast.error(
                CALLBACK_REASONS[reason] ||
                    (reason ? `Anslutningen misslyckades (${reason}).` : 'Anslutningen misslyckades.')
            );
        }

        params.delete('bank_connected');
        params.delete('reason');
        params.delete('select_accounts');
        const remaining = params.toString();
        history.replace({
            pathname: location.pathname,
            search: remaining ? `?${remaining}` : '',
            hash: location.hash,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const handleConnect = async (bankName) => {
        if (!bankName) return;
        try {
            setActionLoading('connect');
            const data = await startConnect(companyId, bankName);
            if (data?.authorizationUrl) {
                // Full-page redirect into the bank's BankID flow — no popup;
                // the round-trip back to /settings is what we want.
                window.location.href = data.authorizationUrl;
            }
        } catch (err) {
            console.error('Error starting bank authorization:', err);
            const code = err?.response?.data?.code;
            toast.error(
                code === 'not_configured'
                    ? 'Bankkoppling är inte aktiverad i denna miljö.'
                    : 'Kunde inte starta bankanslutningen'
            );
        } finally {
            setActionLoading(null);
        }
    };

    const handleSync = async (connectionId, retryHistory = false) => {
        try {
            setActionLoading(connectionId);
            const result = await syncNow(companyId, connectionId, { retryHistory });
            if (result.ok) {
                const imported = result.imported || 0;
                if (result.historyIncomplete) {
                    const remaining = result.unresolvedTransactions;
                    const message = remaining
                        ? `${remaining} ${remaining === 1 ? 'transaktion behöver' : 'transaktioner behöver'} fortfarande kontrolleras.`
                        : 'Historiken behöver fortfarande kontrolleras.';
                    toast.warning(`${imported > 0 ? `${imported} nya transaktioner hämtade.` : 'Inga nya transaktioner hämtades.'} ${message}`);
                } else toast.success(
                    imported > 0
                        ? `${imported} nya transaktioner hämtade.`
                        : retryHistory ? 'Historiken har kontrollerats. Inga kvarstående importfel.' : 'Uppdaterat — inga nya transaktioner.'
                );
            } else {
                toast.error(syncFailureMessage(result));
            }
            loadConnections();
        } catch (err) {
            const data = err?.response?.data;
            const msg = data?.status ? syncFailureMessage(data) : data?.error || 'Hämtningen misslyckades';
            toast.error(msg);
            loadConnections();
        } finally {
            setActionLoading(null);
        }
    };

    const limitToast = (err) => {
        if (err?.response?.data?.code === 'account_limit') {
            toast.error('Din plan tillåter ett synkat bankkonto — uppgradera för att synka fler.');
            return true;
        }
        return false;
    };

    const handleToggleAccount = async (connectionAccountId, enabled) => {
        try {
            await setAccountEnabled(companyId, connectionAccountId, enabled);
            setConnections((prev) =>
                prev.map((conn) => ({
                    ...conn,
                    accounts: (conn.accounts || []).map((a) =>
                        a.connection_account_id === connectionAccountId ? { ...a, enabled } : a
                    ),
                }))
            );
            toast.success(enabled ? 'Kontot synkas igen.' : 'Kontot är undantaget från synk.');
        } catch (err) {
            console.error('Error toggling account:', err);
            if (!limitToast(err)) toast.error('Kunde inte ändra kontot');
        }
    };

    const handleSaveSelection = async () => {
        if (selectedAccounts.length === 0) {
            toast.info('Välj minst ett konto att synka.');
            return;
        }
        try {
            setSavingSelection(true);
            for (const accountId of selectedAccounts) {
                await setAccountEnabled(companyId, accountId, true);
            }
            toast.success('Konton valda — vi hämtar historiken i bakgrunden.');
            setSelectConnectionId(null);
            setSelectedAccounts([]);
            loadConnections();
        } catch (err) {
            if (!limitToast(err)) toast.error('Kunde inte aktivera kontot');
        } finally {
            setSavingSelection(false);
        }
    };

    const selectConnection = connections.find((c) => c.connection_id === selectConnectionId);

    const handleDisconnect = async (connectionId) => {
        try {
            setActionLoading(connectionId);
            await disconnect(companyId, connectionId);
            toast.success('Bankanslutningen är borttagen och medgivandet återkallat.');
            loadConnections();
        } catch (err) {
            console.error('Error disconnecting bank:', err);
            toast.error('Kunde inte ta bort anslutningen');
        } finally {
            setActionLoading(null);
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div className="modern-card">
            <div
                className="modern-card-header"
                style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
            >
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <h2 className="section-title" style={{ marginBottom: 4 }}>
                        <Landmark size={16} /> Bankanslutningar
                    </h2>
                    <p className="section-subtitle" style={{ margin: 0, maxWidth: 560 }}>
                        Anslut företagets bankkonton med BankID så hämtas transaktionerna automatiskt
                        till <a href={`/${companyId}/bankstatements`}>Bankhändelser</a>.
                    </p>
                </div>
                <Button
                    variant="primary"
                    onClick={() => setShowConnectModal(true)}
                    disabled={!providerConfigured}
                    style={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <Plus size={16} /> Anslut bank
                </Button>
            </div>

            <div className="modern-card-body">
                {!providerConfigured && (
                    <Alert variant="warning" style={{ fontSize: 13 }}>
                        <AlertTriangle size={14} className="me-2" />
                        Bankkoppling är inte aktiverad i denna miljö ännu.
                    </Alert>
                )}


                {connections.length === 0 ? (
                    <div className="text-center py-5">
                        <Landmark size={44} style={{ color: '#9ca3af', marginBottom: 12 }} />
                        <h5 style={{ color: '#6b7280' }}>Ingen bank ansluten</h5>
                        <p className="text-muted" style={{ fontSize: 13, maxWidth: 420, margin: '0 auto 16px' }}>
                            Anslut företagets bank med BankID så hämtas transaktionerna automatiskt.
                        </p>
                        <Button variant="primary" onClick={() => setShowConnectModal(true)} disabled={!providerConfigured}>
                            <Plus size={16} className="me-1" /> Anslut din första bank
                        </Button>
                    </div>
                ) : (
                    connections.map((conn) => {
                        const badge = STATUS_BADGES[conn.status];
                        const acting = actionLoading === conn.connection_id;
                        const needsRenewal = conn.status === 'expiring' || conn.status === 'expired';
                        return (
                            <div
                                key={conn.connection_id}
                                style={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    padding: 16,
                                    marginBottom: 12,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            <BankLogo
                                                bank={{
                                                    name: conn.aspsp_name,
                                                    logoUrl: banks.find((b) => b.name === conn.aspsp_name)?.logoUrl || null,
                                                }}
                                            />
                                            <h5 style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {conn.aspsp_name}
                                            </h5>
                                            {badge && <Badge bg={badge.variant}>{badge.label}</Badge>}
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: 12,
                                                color: '#6b7280',
                                                fontSize: 12,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {conn.last_sync_at && <span>Senast hämtad: {formatDate(conn.last_sync_at)}</span>}
                                            {conn.consent_valid_until && (
                                                <span style={needsRenewal ? { color: '#b45309', fontWeight: 600 } : undefined}>
                                                    <Clock size={12} style={{ marginRight: 4, verticalAlign: -2 }} />
                                                    Medgivande t.o.m. {formatDate(conn.consent_valid_until, 'yyyy-MM-dd')}
                                                    {typeof conn.days_until_expiry === 'number' &&
                                                        conn.days_until_expiry >= 0 &&
                                                        ` (${conn.days_until_expiry} dagar kvar)`}
                                                </span>
                                            )}
                                        </div>
                                        {conn.status === 'error' && conn.last_error && (
                                            <Alert variant="danger" className="mt-2 mb-0 py-1 px-2" style={{ fontSize: 12 }}>
                                                <AlertTriangle size={12} className="me-1" />
                                                {conn.last_error_code ? <strong>{conn.last_error_code}:</strong> : null}{' '}
                                                {conn.last_error}
                                            </Alert>
                                        )}
                                        {conn.status === 'expired' && (
                                            <Alert variant="warning" className="mt-2 mb-0 py-1 px-2" style={{ fontSize: 12 }}>
                                                Medgivandet har gått ut — transaktioner hämtas inte längre. Förnya med
                                                BankID så fortsätter hämtningen där den slutade.
                                            </Alert>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        {needsRenewal && (
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleConnect(conn.aspsp_name)}
                                                disabled={acting || actionLoading === 'connect'}
                                            >
                                                Förnya med BankID
                                            </Button>
                                        )}
                                        {(conn.status === 'active' || conn.status === 'expiring') && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                onClick={() => handleSync(conn.connection_id)}
                                                disabled={acting}
                                                title="Hämta nya transaktioner nu"
                                                style={{ color: 'var(--primary)', padding: 6, lineHeight: 1, borderRadius: 8 }}
                                                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rgba-primary-1)')}
                                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                            >
                                                {acting ? <Spinner animation="border" size="sm" /> : <RefreshCw size={16} />}
                                            </Button>
                                        )}
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={() => handleDisconnect(conn.connection_id)}
                                            disabled={acting}
                                            title="Koppla från och återkalla medgivandet"
                                            style={{ color: '#dc2626', padding: 6, lineHeight: 1, borderRadius: 8 }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = '#fee2e2')}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                        >
                                            <Trash2 size={16} />
                                        </Button>
                                    </div>
                                </div>

                                {(conn.accounts || []).length > 0 && (
                                    <div className="mt-3" style={{ borderTop: '1px solid #f3f4f6', paddingTop: 4 }}>
                                        {conn.accounts.map((account) => {
                                            const backfill = BACKFILL_LABELS[account.backfill_status];
                                            const m = mappingFor(account);
                                            return (
                                                <div
                                                    key={account.connection_account_id}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        gap: 16,
                                                        padding: '10px 0',
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    <div style={{ minWidth: 0, display: 'grid', gap: 3 }}>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                                            <span
                                                                style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#0f172a' }}
                                                                title={account.clearing_number
                                                                    ? `Clearingnummer ${account.clearing_number} · Kontonummer ${account.account_number}`
                                                                    : undefined}
                                                            >
                                                                {account.clearing_number
                                                                    ? `${account.clearing_number}-${account.account_number}`
                                                                    : account.account_number || account.iban || '—'}
                                                            </span>
                                                            {(account.display_name || account.currency) && (
                                                                <span style={{ color: '#6b7280', fontSize: 12 }}>
                                                                    {[account.display_name, account.currency].filter(Boolean).join(' · ')}
                                                                </span>
                                                            )}
                                                            {account.backfill_status === 'running' && (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280' }}>
                                                                    <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                                                                    Hämtar historik…
                                                                </span>
                                                            )}
                                                            {backfill && <Badge bg={backfill.variant}>{backfill.label}</Badge>}
                                                        </div>
                                                        {account.iban && (
                                                            <div style={{ fontSize: 11, color: '#9ca3af', fontVariantNumeric: 'tabular-nums' }}>
                                                                IBAN {account.iban}
                                                            </div>
                                                        )}
                                                        <div>
                                                            {m ? (
                                                                <span style={{ fontSize: 12, fontWeight: 600, color: '#15803d', background: '#f0fdf4', borderRadius: 6, padding: '2px 8px' }}>
                                                                    Bokförs mot {m.accounting_number} {m.accounting_name}
                                                                </span>
                                                            ) : (
                                                                <span style={{ fontSize: 12, color: '#b45309' }}>
                                                                    Ej kopplat till kontoplanen —{' '}
                                                                    <a href={`/${companyId}/bank-accounts`} style={{ color: '#b45309', fontWeight: 600 }}>
                                                                        koppla under Konton
                                                                    </a>
                                                                </span>
                                                            )}
                                                        </div>
                                                        <BankImportWarning
                                                            account={account}
                                                            companyId={companyId}
                                                            onRetry={() => handleSync(conn.connection_id, true)}
                                                            retryDisabled={acting || !account.enabled || account.backfill_status === 'running' || !['active', 'expiring'].includes(conn.status)}
                                                        />
                                                    </div>
                                                    {(conn.accounts.length > 1 || !account.enabled) && (
                                                        <Form.Check
                                                            type="switch"
                                                            id={`bank-acct-${account.connection_account_id}`}
                                                            label="Synka"
                                                            title="Av: kontot hämtas inte längre automatiskt — medgivandet och redan hämtad historik behålls."
                                                            checked={!!account.enabled}
                                                            onChange={(e) =>
                                                                handleToggleAccount(account.connection_account_id, e.target.checked)
                                                            }
                                                            style={{ flexShrink: 0, marginTop: 2 }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Post-consent account selection (multi-account consents). Not
                dismissable by accident: choosing is the completion of connect. */}
            <Modal show={Boolean(selectConnection)} onHide={() => setSelectConnectionId(null)} centered backdrop="static">
                <Modal.Header closeButton={!savingSelection}>
                    <Modal.Title style={{ fontSize: 17 }}>Välj konton att synka</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: 13, color: '#6b7280' }}>
                        {allowMultiAccounts
                            ? 'Välj vilka konton som ska hämtas automatiskt. ' + (maxAccounts === null ? 'Ditt paket har obegränsat antal bankkonton.' : `Ditt paket omfattar ${maxAccounts} bankkonton.`)
                            : 'Ditt paket synkar ett bankkonto. Lägg till fler bankkonton eller byt plan för att synka fler.'}
                    </p>
                    {(selectConnection?.accounts || []).map((account) => {
                        const checked = selectedAccounts.includes(account.connection_account_id);
                        return (
                            <label
                                key={account.connection_account_id}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                                    border: '1px solid ' + (checked ? 'var(--primary)' : '#e5e7eb'),
                                    borderRadius: 10, marginBottom: 8, cursor: 'pointer', width: '100%',
                                }}
                            >
                                <Form.Check
                                    type={allowMultiAccounts ? 'checkbox' : 'radio'}
                                    name="select-bank-account"
                                    checked={checked}
                                    onChange={() =>
                                        setSelectedAccounts((prev) =>
                                            allowMultiAccounts
                                                ? checked ? prev.filter((id) => id !== account.connection_account_id) : [...prev, account.connection_account_id]
                                                : [account.connection_account_id]
                                        )
                                    }
                                />
                                <span style={{ minWidth: 0 }}>
                                    <span style={{ display: 'block', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                                        {account.clearing_number ? account.clearing_number + '-' + account.account_number : account.account_number || account.iban}
                                    </span>
                                    <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                        {[account.display_name, account.currency, account.iban].filter(Boolean).join(' · ')}
                                    </span>
                                </span>
                            </label>
                        );
                    })}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6' }}>
                    <Button variant="outline-secondary" size="sm" onClick={() => setSelectConnectionId(null)} disabled={savingSelection}>
                        Senare
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSaveSelection} disabled={savingSelection}>
                        {savingSelection ? <Spinner animation="border" size="sm" /> : 'Börja synka'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <BankConnectModal
                show={showConnectModal}
                onHide={() => setShowConnectModal(false)}
                banks={banks}
                banksLoading={banksLoading}
                connecting={actionLoading === 'connect'}
                onConnect={handleConnect}
            />
        </div>
    );
};

export default BankConnectionSettings;
